import { memo } from 'react';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import { ModlistDiffResponse } from '../../types/apogee';
import { cn } from '../../utils/cn';

interface ModlistDiffViewerProps {
  diff: ModlistDiffResponse;
  className?: string;
}

export const ModlistDiffViewer = memo(({ diff, className }: ModlistDiffViewerProps) => {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary row */}
      <div className="flex items-center gap-3 text-sm tabular-nums">
        <span className="eyebrow">Mod count</span>
        <span className="font-mono text-glass-300">{diff.from_count}</span>
        <ArrowRight className="w-4 h-4 text-glass-400" />
        <span className="font-mono text-glass-300">{diff.to_count}</span>
        <span className="text-glass-500">
          ({diff.to_count - diff.from_count >= 0 ? '+' : ''}{diff.to_count - diff.from_count})
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Added */}
        <div className="subcard p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4 text-green-400" />
            <span className="font-medium text-green-300 tabular-nums">Added ({diff.mods_added.length})</span>
          </div>
          {diff.mods_added.length === 0 ? (
            <p className="text-xs text-glass-500">No mods added</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {diff.mods_added.map((modId) => (
                <span
                  key={modId}
                  className="chip font-mono tabular-nums bg-green-500/10 text-green-300 border-green-500/20"
                >
                  {modId}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Removed */}
        <div className="subcard p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <Minus className="w-4 h-4 text-red-400" />
            <span className="font-medium text-red-300 tabular-nums">Removed ({diff.mods_removed.length})</span>
          </div>
          {diff.mods_removed.length === 0 ? (
            <p className="text-xs text-glass-500">No mods removed</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {diff.mods_removed.map((modId) => (
                <span
                  key={modId}
                  className="chip font-mono tabular-nums bg-red-500/10 text-red-300 border-red-500/20"
                >
                  {modId}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

ModlistDiffViewer.displayName = 'ModlistDiffViewer';
